// Everything the app keeps in memory or storage FOR THE SIGNED-IN USER, and how to drop it when the
// user changes. The workspace list is the obvious one (a super-admin's platform-wide list must never be
// shown to the next person to sign in on this browser, nor a customer's short list to staff), but the
// galleries / collections / plan queries, the uploader's remembered destinations and the queue are all
// per-user too. CognitoAuthProvider calls clearPerUserCaches() whenever the identity actually sending
// requests (the id token's `sub`) moves away from a signed-in user — sign-out, sign-in as someone else,
// or a sign-in in another tab of this origin (Amplify keeps ONE session per origin in localStorage).
import type { QueryClient } from "@tanstack/react-query";
import { decodeJwtClaims } from "@/lib/tokenExpiry";

/** The identity requests are actually made as: the id token's `sub` (null when signed out). */
export const identityOf = (idToken: string | null | undefined): string | null => {
  const sub = decodeJwtClaims(idToken)?.sub;
  return typeof sub === "string" && sub ? sub : null;
};

// The WorkspaceProvider's boot snapshot (see useWorkspace). Lives here so clearing it needs no import of
// the hook (which itself depends on the auth provider).
const workspaceSnapshots = new Map<string, unknown>();
export const workspaceSnapshotStore = {
  get: (key: string) => workspaceSnapshots.get(key),
  set: (key: string, value: unknown) => void workspaceSnapshots.set(key, value),
  delete: (key: string) => void workspaceSnapshots.delete(key),
};

/** localStorage key prefixes holding one user's uploader destinations (galleries / collections). */
export const PER_USER_STORAGE_PREFIXES = ["ph_up_resume:", "ph_up_watch:", "ph_up_launch_collection:"];
/** sessionStorage keys holding one user's state: the uploader's, and the dismissed "money waiting" banner. */
export const PER_USER_SESSION_KEYS = ["ph_up_cloud_return", "ph_earnings_banner_dismissed"];

const dropKeys = (store: Storage | undefined, match: (k: string) => boolean) => {
  if (!store) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && match(k)) doomed.push(k);
    }
    doomed.forEach((k) => store.removeItem(k));
  } catch {
    /* storage unavailable (private mode, SSR) — nothing to clear */
  }
};

/** Drop every per-user cache: React Query (all queries), the workspace snapshot, uploader storage. */
export const clearPerUserCaches = (queryClient?: QueryClient | null) => {
  try {
    queryClient?.cancelQueries();
    queryClient?.clear();
  } catch {
    /* ignore */
  }
  workspaceSnapshots.clear();
  if (typeof window === "undefined") return;
  dropKeys(window.localStorage, (k) => PER_USER_STORAGE_PREFIXES.some((p) => k.startsWith(p)));
  dropKeys(window.sessionStorage, (k) => PER_USER_SESSION_KEYS.includes(k));
};

/** A storage event that means "the signed-in user of this origin may have changed" (another tab). */
export const isAuthStorageKey = (key: string | null): boolean =>
  key === null || // localStorage.clear()
  key === "token" || // legacy admin's shared session
  /^CognitoIdentityServiceProvider\..+\.(LastAuthUser|idToken)$/.test(key);
